import { UploadLog } from '@/common/api/params/upload'
import { CSV_FORMATS } from '@/common/app/modules/csv'
import {
  isRetryableUpload,
  uploadSourceBadgeType,
  uploadSourceLabel,
  uploadStatusBadgeType,
  uploadStatusLabel
} from '@/common/app/modules/upload'
import { dateTimeStr, durationStr } from '@/common/util/date'

import Badge from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

interface Props {
  log: UploadLog
  retrying: boolean
  onRetry: (log: UploadLog) => void
  onShowErrors: (log: UploadLog) => void
}

const UploadLogListRow = ({ log, retrying, onRetry, onShowErrors }: Props) => {
  const masterLabel = CSV_FORMATS.find((f) => f.type === log.master_type)?.label || log.master_type
  // 受付→開始が待ち時間、開始→終了が処理時間（upload-log-timings）
  const waiting = durationStr(log.uploaded_at, log.started_at)
  const processing = durationStr(log.started_at, log.processed_at)

  return (
    <TableRow>
      <TableRowItem>
        <Text>{log.id}</Text>
      </TableRowItem>
      <TableRowItem width={200}>
        <Text truncate>{log.filename}</Text>
      </TableRowItem>
      <TableRowItem>
        <Text nowrap>{masterLabel}</Text>
      </TableRowItem>
      <TableRowItem>
        <Badge badgeType={uploadStatusBadgeType(log.status)}>{uploadStatusLabel(log.status)}</Badge>
      </TableRowItem>
      <TableRowItem>
        <Text>{log.row_count}</Text>
      </TableRowItem>
      <TableRowItem>
        <Text>{log.created_count}</Text>
      </TableRowItem>
      <TableRowItem>
        <Text>{log.updated_count}</Text>
      </TableRowItem>
      <TableRowItem>
        <Text>{log.skipped_count}</Text>
      </TableRowItem>
      <TableRowItem>
        {/* 削除件数が無いログ（バックエンド反映前など）は他の件数列と同じく 0 で表示する */}
        <Text>{log.deleted_count ?? 0}</Text>
      </TableRowItem>
      <TableRowItem>
        <ItemAlign gapX={4}>
          <Text color={log.error_count > 0 ? TextColor.CRITICAL : TextColor.PRIMARY}>
            {log.error_count}
          </Text>
          {log.error_count > 0 && (
            <Button color={ButtonColor.TERTIARY} onClick={() => onShowErrors(log)}>
              詳細
            </Button>
          )}
        </ItemAlign>
      </TableRowItem>
      <TableRowItem>
        <Badge badgeType={uploadSourceBadgeType(log.upload_source)}>
          {uploadSourceLabel(log.upload_source)}
        </Badge>
      </TableRowItem>
      {[log.uploaded_at, log.started_at, log.processed_at].map((at, i) => (
        <TableRowItem key={i}>
          {at ? <Text nowrap>{dateTimeStr(at)}</Text> : <Text color={TextColor.SECONDARY}>-</Text>}
        </TableRowItem>
      ))}
      {[waiting, processing].map((d, i) => (
        <TableRowItem key={i}>
          {d ? <Text nowrap>{d}</Text> : <Text color={TextColor.SECONDARY}>-</Text>}
        </TableRowItem>
      ))}
      <TableRowItem>
        {isRetryableUpload(log.status) && (
          <Button
            color={ButtonColor.TERTIARY}
            loading={retrying}
            disabled={retrying}
            onClick={() => onRetry(log)}
          >
            リトライ
          </Button>
        )}
      </TableRowItem>
    </TableRow>
  )
}

export default UploadLogListRow
